import { useState } from "react"
import { useContentfulLiveUpdates } from "@contentful/live-preview/react"

// ---------------------------------------------------------

import FlexibleData from "@lib/flexibleData"
import { getIndexDataByTags, getAllCustomersData } from "@lib/api"
import FlexiblePage from "@templates/flexible/template"

import Button from "@components/button"
import Card from "@components/card"
import CustomerCard from "@components/card-rounded/card-customer"

// ---------------------------------------------------------

import {
  button_container,
  cards_header,
  cards_spotlight_container,
  customer_stories_container,
  case_studies_container,
  no_results
} from "./styles.module.scss"

// ---------------------------------------------------------

const LIMIT = 12

export async function getStaticProps(context) {
  const preview = process.env.NODE_ENV === "development" || context.draftMode || false
  const previewEnv = context?.previewData?.environment || null
  const modules = (await FlexibleData.fetch("customers", preview, previewEnv)) ?? []

  // Get any pages that are tagged customerSpotlight
  const indexRes = (await getIndexDataByTags(["customerSpotlight"])) ?? []
  const indexData = indexRes.data.entryCollection.items

  // Get all resources or pages of type case study/customer story
  const customerData = (await getAllCustomersData()) ?? []

  return {
    props: { preview, modules, indexData, customerData }
  }
}

// ---------------------------------------------------------

const CustomerIndex = ({ preview, indexData, modules, customerData }) => {
  const liveModules = useContentfulLiveUpdates(modules)
  const [endPost, setEndPost] = useState(LIMIT)
  const filteredPosts = indexData.slice(0, endPost)

  return (
    <FlexiblePage data={liveModules} preview={preview}>
      <h2 className={cards_header}>Customer Stories</h2>

      <div className={customer_stories_container}>
        {customerData?.pages?.map((customerAsset, i) => {
          // Retrieves all customer pages
          return (
            <div key={i}>
              <CustomerCard
                link={`/customers/${customerAsset.slug}`}
                image={{
                  alt: customerAsset.logo?.description,
                  height: 100,
                  src: customerAsset.logo?.url,
                  width: 300
                }}
                title={customerAsset.title}
                tags={customerAsset?.contentfulMetadata?.tags.slice(0, 2)}
                imageDescription={customerAsset.imageDescription}
              />
            </div>
          )
        })}
      </div>

      <h2 className={cards_header}>Customer Resources</h2>
      <div className={case_studies_container}>
        {customerData?.resources?.map((customerAsset, i) => {
          const getResourceTags = () => {
            let tags = customerAsset?.tags?.tags
            return tags.map((tag) => {
              return tag.name.replace("Industry: ", "")
            })
          }

          // Retrieves all customer case studies
          return (
            <div key={i}>
              <CustomerCard
                link={`/resources/${customerAsset.slug}`}
                image={{
                  alt: customerAsset.logo?.description,
                  height: 100,
                  src: customerAsset.logo?.url,
                  width: 300
                }}
                title={customerAsset.title}
                tags={getResourceTags()}
              />
            </div>
          )
        })}
      </div>

      {/* Returns all customer spotlights */}

      <h2 className={cards_header}>Customer Spotlights</h2>

      <div className={cards_spotlight_container}>
        {/* Iterate through each use case group */}
        {filteredPosts.map((card, i) => {
          return (
            <div key={i}>
              <Card
                buttonUrl={`/blog/${card.slug}`}
                description={card.excerpt}
                image={{
                  alt: card.featuredImage?.description,
                  height: 700,
                  src: card.featuredImage?.url,
                  width: 1080
                }}
                theme="hover"
                title={card.title}
                label="Blog"
              />
            </div>
          )
        })}
      </div>

      <div className={button_container}>
        {filteredPosts?.length < 1 && <p className={no_results}>No Results</p>}
        {endPost < indexData.length - 1 && (
          <Button theme="dark" onClick={() => setEndPost(endPost + LIMIT)}>
            Show more
          </Button>
        )}
      </div>
    </FlexiblePage>
  )
}

export default CustomerIndex
